<template>
  <component
    :is="tag"
    ref="root"
    class="card-surface"
    :class="surfaceClasses"
    :style="surfaceStyle"
    @pointerenter="onSheen && onPointerEnter($event)"
    @pointermove="onSheen && onPointerMove($event)"
    @pointerleave="onSheen && onPointerLeave()"
  >
    <div class="card-surface__body" :class="bodyPadClass">
      <slot />
    </div>
  </component>
</template>

<script setup>
import { computed } from 'vue'
import { useCardSheen } from '@/composables/useCardSheen'

const props = defineProps({
  tag: { type: String, default: 'div' },
  featured: { type: Boolean, default: false },
  raised: { type: Boolean, default: false },
  inset: { type: Boolean, default: false },
  hoverable: { type: Boolean, default: true },
  sheen: { type: Boolean, default: true },
  /** none | sm | md | lg | xl — inner body padding */
  pad: { type: String, default: 'md' },
})

const bodyPadClass = computed(() => `card-surface__body--pad-${props.pad}`)

const onSheen = computed(() => !props.inset && props.sheen)

const { root, surfaceStyle: sheenStyle, onPointerMove, onPointerEnter, onPointerLeave } = useCardSheen()

const surfaceClasses = computed(() => ({
  'nm-panel': true,
  'nm-panel--raised': props.raised,
  'nm-panel--inset': props.inset,
  'nm-panel--featured': props.featured,
  'nm-sheen': onSheen.value,
  'card-surface--featured': props.featured,
  'card-surface--hoverable': props.hoverable && !props.inset,
  'card-surface--flat': props.inset || !props.sheen,
}))

const surfaceStyle = computed(() => (onSheen.value ? sheenStyle.value : undefined))
</script>

<style scoped>
.card-surface {
  position: relative;
  overflow: visible;
  isolation: isolate;
  background: var(--nm-surface);
  transition:
    transform var(--nm-duration) var(--nm-ease-out),
    box-shadow var(--nm-duration) var(--nm-ease-out),
    border-color var(--nm-duration) var(--nm-ease-out);
}

.card-surface--featured {
  background: linear-gradient(155deg, var(--nm-surface-raised) 0%, var(--nm-surface) 100%);
}

.card-surface--flat {
  background: var(--nm-surface-inset) !important;
}

.card-surface--hoverable:hover {
  transform: translateY(-4px);
  box-shadow: var(--nm-raised-lg);
  border-color: rgba(255, 255, 255, 0.07);
  z-index: 1;
}

.card-surface--featured.card-surface--hoverable:hover {
  transform: translateY(-5px);
  box-shadow: var(--nm-raised-xl), var(--nm-glow-accent);
}

.card-surface__body {
  position: relative;
  z-index: 1;
  height: 100%;
}

.card-surface.d-flex .card-surface__body {
  display: flex;
  flex-direction: inherit;
  flex-wrap: inherit;
  align-items: inherit;
  justify-content: inherit;
  gap: inherit;
  width: 100%;
}

.card-surface__body--pad-none {
  padding: 0;
}

.card-surface__body--pad-sm {
  padding: 1rem;
}

.card-surface__body--pad-md {
  padding: 1.25rem;
}

@media (min-width: 768px) {
  .card-surface__body--pad-md {
    padding: 2rem;
  }
}

.card-surface__body--pad-lg {
  padding: 1.75rem;
}

@media (min-width: 768px) {
  .card-surface__body--pad-lg {
    padding: 2.5rem;
  }
}

.card-surface__body--pad-xl {
  padding: clamp(2.25rem, 5vw, 3.5rem);
}
</style>